import {
  ArrowDown,
  ArrowUp,
  Bot,
  CalendarDays,
  Check,
  User,
  X
} from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import type { PlanTodo } from "../types";
import { formatDueDate, isAgentTodo, isOverdue, todoProgress } from "./model";
import { PLAN_ACCENT_TEXT } from "./theme";

type Props = {
  todos: readonly PlanTodo[];
  /** Section label; `null` hides the header row. */
  label?: string | null;
  /** 12px rows (chat card) or 13px rows (the plan document). */
  size?: "sm" | "md";
  className?: string;
  /** Edit affordances (plan pane in edit mode): shown on hover. */
  onRemove?: (id: string) => void;
  onMove?: (id: string, direction: -1 | 1) => void;
};

/** Who the to-do is for and when it is due, after its text. */
const TodoMeta: FC<{ todo: PlanTodo; closed: boolean }> = ({
  todo,
  closed
}) => {
  if (!todo.owner && !todo.due) return null;
  const person = !isAgentTodo(todo);
  const late = Boolean(todo.due && !closed && isOverdue(todo.due));
  return (
    <span
      className="ml-2 inline-flex items-center gap-1.5 align-baseline text-xs whitespace-nowrap text-muted-foreground"
      data-testid="todo-meta"
    >
      {todo.owner ? (
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-sm px-1 py-px",
            person ? "bg-primary/10 text-primary" : "bg-muted"
          )}
          data-testid="todo-owner"
        >
          {person ? (
            <User className="size-2.5" aria-hidden />
          ) : (
            <Bot className="size-2.5" aria-hidden />
          )}
          {todo.owner}
        </span>
      ) : null}
      {todo.due ? (
        <span
          className={cn(
            "inline-flex items-center gap-1",
            late && "text-destructive"
          )}
          data-testid="todo-due"
        >
          <CalendarDays className="size-2.5" aria-hidden />
          {late ? "Overdue " : "Due "}
          {formatDueDate(todo.due)}
        </span>
      ) : null}
    </span>
  );
};

/**
 * A plan's to-dos the way Cursor's plan editor lists them: a header row with
 * the count, then one line per item with a small square box. Pending is an
 * empty box, in progress a spinner in the accent, completed a filled box
 * with a check and struck-through text, cancelled a dashed box.
 */
export const PlanTodos: FC<Props> = ({
  todos,
  label = "To-dos",
  size = "md",
  className,
  onRemove,
  onMove
}) => {
  const progress = todoProgress(todos);
  const editable = Boolean(onRemove || onMove);
  return (
    <div
      className={cn(
        "rounded-lg border border-line-2 bg-fill-1",
        size === "sm" ? "text-sm" : "text-base",
        className
      )}
      data-testid="plan-todos"
    >
      {label !== null ? (
        <div className="flex items-center justify-between gap-2 border-b border-line-2 px-3 py-1.5">
          <span className="font-medium text-fg-2">{label}</span>
          <span
            className="text-xs text-muted-foreground"
            data-testid="plan-todos-progress"
          >
            {progress.label}
          </span>
        </div>
      ) : null}
      <ul className="px-2 py-1">
        {todos.map((todo, index) => {
          const done = todo.status === "completed";
          const cancelled = todo.status === "cancelled";
          const active = todo.status === "in_progress";
          return (
            <li
              key={todo.id}
              data-testid={`todo-${todo.id}`}
              data-status={todo.status}
              className="group flex items-start gap-2.5 rounded-md px-1.5 py-1 leading-[1.5]"
            >
              <span
                aria-hidden={!active}
                aria-label={active ? "In progress" : undefined}
                className={cn(
                  "mt-0.75 inline-flex size-3.5 shrink-0 items-center justify-center rounded-xs border",
                  done
                    ? "border-foreground/70 bg-fill-4 text-background"
                    : cancelled
                    ? "border-dashed border-muted-foreground/50"
                    : active
                    ? cn("border-warn-line", PLAN_ACCENT_TEXT)
                    : "border-muted-foreground/45"
                )}
              >
                {done ? (
                  <Check className="size-2.5" strokeWidth={3} />
                ) : active ? (
                  <Spinner className="size-2.5" />
                ) : null}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "break-words",
                    done && "text-muted-foreground line-through",
                    cancelled && "text-muted-foreground italic line-through",
                    active && "text-foreground"
                  )}
                >
                  {todo.content}
                </span>
                <TodoMeta todo={todo} closed={done || cancelled} />
              </span>
              {editable ? (
                <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  {onMove ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="size-5 text-muted-foreground"
                        aria-label="Move up"
                        disabled={index === 0}
                        onClick={() => onMove(todo.id, -1)}
                      >
                        <ArrowUp className="size-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="size-5 text-muted-foreground"
                        aria-label="Move down"
                        disabled={index === todos.length - 1}
                        onClick={() => onMove(todo.id, 1)}
                      >
                        <ArrowDown className="size-3" />
                      </Button>
                    </>
                  ) : null}
                  {onRemove ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="size-5 text-muted-foreground"
                      aria-label="Remove todo"
                      onClick={() => onRemove(todo.id)}
                    >
                      <X className="size-3" />
                    </Button>
                  ) : null}
                </span>
              ) : null}
            </li>
          );
        })}
        {todos.length === 0 ? (
          <li className="px-1.5 py-1 text-muted-foreground italic">
            No to-dos yet.
          </li>
        ) : null}
      </ul>
    </div>
  );
};
